import { useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, setToken, clearToken, queryClient, getQueryFn } from "../lib/api";
import { Sentry } from "../lib/sentry";

interface AdminUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
}

export function useAdminAuth() {
  const {
    data: admin,
    isLoading,
    error: _error,
  } = useQuery<AdminUser | null>({
    queryKey: ["/api/admin/auth/me"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    retry: false,
    staleTime: Infinity,
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const res = await apiRequest("POST", "/api/admin/auth/login", credentials);
      const data = await res.json();
      setToken(data.token);
      return data.admin as AdminUser;
    },
    onSuccess: (admin) => {
      queryClient.setQueryData(["/api/admin/auth/me"], admin);
    },
  });

  const logout = () => {
    clearToken();
    queryClient.setQueryData(["/api/admin/auth/me"], null);
    queryClient.clear();
    window.location.href = "/login";
  };

  useEffect(() => {
    if (admin) {
      Sentry.setUser({ id: admin.id, email: admin.email });
    } else {
      Sentry.setUser(null);
    }
  }, [admin]);

  return {
    admin: admin ?? null,
    isLoading,
    isAuthenticated: !!admin,
    login: loginMutation.mutateAsync,
    loginError: loginMutation.error,
    isLoggingIn: loginMutation.isPending,
    logout,
  };
}
